import type { HighlightComment } from "@/types/content";
import { formatNumber } from "@/utils/format";

interface CommentCardProps {
  comment: HighlightComment;
  onLike?: (id: string) => void;
  liked?: boolean;
}

const badgeTone: Record<string, string> = {
  神评: "bg-accent-500 text-background-50",
  高能: "bg-primary-500 text-background-50",
  热评: "bg-secondary-500 text-background-50",
};

export default function CommentCard({ comment, onLike, liked = false }: CommentCardProps) {
  const initial = comment.nickname.slice(0, 1);
  return (
    <article className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-base font-bold ${comment.avatarClass}`}
        aria-hidden="true"
      >
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-label text-sm font-medium text-foreground-900">
            {comment.nickname}
          </span>
          {comment.badge && (
            <span
              className={`rounded-full px-2 py-0.5 font-label text-[11px] leading-none ${badgeTone[comment.badge] ?? "bg-background-200 text-foreground-700"}`}
            >
              {comment.badge}
            </span>
          )}
          <span className="font-label text-xs text-foreground-400">{comment.time}</span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
        <div className="mt-3 flex items-center gap-5 font-label text-xs text-foreground-500">
          <button
            type="button"
            onClick={() => onLike?.(comment.id)}
            className={`inline-flex cursor-pointer items-center gap-1 transition-colors ${
              liked ? "text-primary-600" : "hover:text-primary-600"
            }`}
            aria-pressed={liked}
            aria-label={`点赞 ${comment.nickname} 的评论`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} aria-hidden="true" />
            </span>
            {formatNumber(comment.likes + (liked ? 1 : 0))}
          </button>
          <span className="inline-flex items-center gap-1">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-chat-3-line" aria-hidden="true" />
            </span>
            {formatNumber(comment.replies)} 条回复
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-share-forward-line" aria-hidden="true" />
            </span>
            转发
          </span>
        </div>
      </div>
    </article>
  );
}